import React, { useEffect, useState } from 'react'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import SaveNotification from 'src/components/common/SaveNotification'
import { postRequest } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { UserProfileState, UserProfileErrorState } from 'src/lib/types/settings'
import { defaultRecipient, defaultUserProfileErrorState } from 'src/assets/SettingsConfig'
import { errorToast } from 'src/lib/utils/toasters'
import 'react-phone-input-2/lib/style.css'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import { roleValues } from 'src/lib/utils/user'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import PhoneNumberInputField from 'src/components/common/form-elements/PhoneNumberInputField'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useRouter } from 'next/router'
import { validateRecipient } from 'src/lib/utils/settings'
import { notificationToast } from 'src/components/common/toaster/NotificationToast'

const MyProfile = () => {
  const dropdownStyle = {
    fontWeight: 400,
    fontSize: 14
  }
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const router = useRouter()
  const { setLoaderProperties } = useLoader()
  const { temporary, setTemporaryData } = useTemporary()
  const [newRecipient, setNewRecipient] = useState<UserProfileState>(defaultRecipient)
  const [initialNewRecipient, setInitialNewRecipient] = useState<UserProfileState>(defaultRecipient)
  const [userProfileErrors, setUserProfileErrors] = useState<UserProfileErrorState>(defaultUserProfileErrorState)
  const [isNavigating, setIsNavigating] = useState(false)

  useEffect(() => {
    if (Boolean(temporary)) {
      setNewRecipient({ ...temporary })
      setTemporaryData(undefined)
    }
  }, [temporary, setTemporaryData])

  useEffect(() => {
    if (isNavigating) {
      router.push('/settings/members')
    }
  }, [isNavigating, router])

  useConfirmOnRouteChanges(
    !isNavigating && JSON.stringify(initialNewRecipient) !== JSON.stringify(newRecipient),
    () => {
      return new Promise((resolve: any, reject) => {
        if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
          setTemporaryData(newRecipient)
          resolve(true)
        } else {
          reject(false)
        }
      })
    },
    newRecipient
  )

  const isValid = () => {
    const errors = validateRecipient(newRecipient)
    setUserProfileErrors(errors)

    return !Object.values(errors).some(error => error)
  }

  const handleSave = async () => {
    if (!isValid()) return
    try {
      setLoaderProperties({ isLoaderVisible: true, message: t('loading') })
      const payload = {
        oids: [newRecipient.email],
        merchant_id: merchant?.accountId,
        metadata: {
          role: newRecipient.role,
          name: newRecipient.firstName + ' ' + newRecipient.lastName,
          phone_number: newRecipient.phoneNumber
        },
        invitation_type: 'RECIPIENT_ACCESS'
      }
      await postRequest('/merchant-account/invitations', apiURI.AUTH_ENGINE, payload)
      setIsNavigating(true)
      notificationToast('Recipient will be added once the email is verified')
    } catch (e: any) {
      console.error(e)
      errorToast(e.error ?? t('something_went_wrong'))
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  return (
    <>
      <div className='flex flex-col space-y-4 w-[50%] mt-4'>
        <LayoutContainer>
          <div className='flex flex-row gap-2.5 pb-0.5'>
            <InputTextField
              handleFocus={() => setUserProfileErrors({ ...userProfileErrors, firstName: false })}
              label={t('first_name')}
              textFieldId='first-name'
              inputValue={newRecipient?.firstName}
              handleChange={e => {
                if (e.target.value?.length > 20) return
                setNewRecipient({
                  ...newRecipient,
                  firstName: e.target.value
                })
              }}
              isRequired={true}
              errorStatus={Boolean(userProfileErrors?.firstName)}
              errorMessage={t('valid_first_name')}
            />

            <InputTextField
              label={t('last_name')}
              textFieldId='last-name'
              inputValue={newRecipient?.lastName === 'N/A' ? '' : newRecipient?.lastName}
              handleChange={e => {
                if (e.target.value?.length > 20) return
                setNewRecipient({
                  ...newRecipient,
                  lastName: e.target.value
                })
              }}
            />
          </div>
          <div className='flex gap-4 '>
            {/* Phone Number Input Field */}
            <div className='flex-1 py-1'>
              <PhoneNumberInputField
                handleFocus={() => setUserProfileErrors({ ...userProfileErrors, phoneNumber: false })}
                label={t('phone_number')}
                isRequired={true}
                inputValue={newRecipient?.phoneNumber}
                handleChange={e =>
                  setNewRecipient({
                    ...newRecipient,
                    phoneNumber: e
                  })
                }
                errorStatus={Boolean(userProfileErrors?.phoneNumber)}
                errorMessage={t('valid_phone_number')}
                onlyCountries={['in']}
                id='phone-number-input-field'
              />
            </div>

            {/* Role Selection Dropdown */}
            <div className='flex-1'>
              <label className='font-medium text-[12px] tracking-[.15px] text-[#6e6e6e] leading-normal'>
                {t('role')}
                <span className='text-[#CB2711]'>*</span>
              </label>
              <Select
                labelId='role-select-label'
                id='role-select'
                value={newRecipient?.role}
                displayEmpty
                onChange={(e: any) => {
                  setNewRecipient({
                    ...newRecipient,
                    role: e.target.value
                  })
                }}
                inputProps={{ style: { fontSize: '14px' } }}
                className='h-10 w-full'
                sx={{ ...dropdownStyle, padding: 0 }}
              >
                <MenuItem value='' disabled>
                  <em>Select role</em>
                </MenuItem>
                {roleValues.map((data: any) => (
                  <MenuItem key={data.label} value={data.value} sx={dropdownStyle}>
                    {data.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          <div className=''>
            <div>
              <InputTextField
                handleFocus={() => setUserProfileErrors({ ...userProfileErrors, email: false })}
                label={t('email')}
                textFieldId='email'
                inputValue={newRecipient?.email}
                handleChange={e => {
                  setNewRecipient({
                    ...newRecipient,
                    email: e.target.value
                  })
                }}
                isRequired={true}
                errorStatus={Boolean(userProfileErrors?.email)}
                errorMessage={t('valid_email')}
              />
            </div>
          </div>
        </LayoutContainer>
      </div>
      <SaveNotification
        isActive={true}
        isVisible={JSON.stringify(initialNewRecipient) !== JSON.stringify(newRecipient)}
        onSave={() => {
          handleSave()
        }}
        onDiscard={() => {
          setNewRecipient({ ...initialNewRecipient })
          setUserProfileErrors(defaultUserProfileErrorState)
        }}
      />
    </>
  )
}

export default MyProfile
